import isEqual from 'lodash/isEqual';
import find from 'lodash/find';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import intersection from 'lodash/intersection';
import findIndex from 'lodash/findIndex';
import map from 'lodash/map';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { renderHTML } from '~/native-ui';
import { localSource } from './localSource';
import { remoteSource } from './remoteSource';
import identify from '~/identify';
import limit from '~/data/limit';
import CollapseText from '~/ui/ReactML/CollapseText';
import { LinkToSpan } from '~/native-ui/Link';
import Icon from '~/ui/ReactML/Icon';
import { Link } from '~/native-ui/Link';
import { Dropdown } from '~/native-ui/Dropdown';
import { FormControl } from '~/native-ui/FormControl';
import { Box } from '~/native-ui/native-base';
import { InlineSelect } from '../../Dropdown';

export class OptionsManager extends Component {
    static defaultProps = {
        minQueryLength: 0,
    };

    static propTypes = {
        children: PropTypes.node.isRequired,
        requestGroup: PropTypes.string.isRequired,
        templateValueClassName: PropTypes.string,
        selectedOptionClassName: PropTypes.string,
        optionClassName: PropTypes.string,

        name: PropTypes.string.isRequired,
        query: PropTypes.string,
        itemId: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.number, PropTypes.string]),
        inputType: PropTypes.string.isRequired,

        invalidateOptions: PropTypes.func,
        fetchOptions: PropTypes.func.isRequired,
        maxValueReloadTries: PropTypes.number,
        isInline: PropTypes.bool,
    };

    static defaultProps = {
        query: null,
        maxValueReloadTries: 1,
        isInline: false,
    };

    valueReloadTries = {
        name: null,
        tries: 0,
    };

    normalizedQuery = () => {
        return this.props.query ? this.props.query : '';
    };

    isQueryValid = () => {
        return this.normalizedQuery().length >= this.props.minQueryLength;
    };

    haveConstraintsChanged = (prevProps) => {
        return !isEqual(this.props.constraintValues, prevProps.constraintValues);
    };

    isOutOfSync(prevProps) {
        let isOutOfSync =
            this.isQueryValid() &&
            (this.props.status === identify.STATUS_INVALIDATED ||
                this.props.query !== prevProps.query ||
                this.props.itemId !== prevProps.itemId ||
                this.props.inputType !== prevProps.inputType ||
                this.props.name !== prevProps.name);

        return isOutOfSync;
    }

    // TODO: set selectedItems correctly
    fetchOptions(selectedItems = this.props.value) {
        limit(this.props.requestGroup, () => this.props.fetchOptions(this.normalizedQuery(), selectedItems));
    }

    fetchOptionsValueReload(selectedItems = this.props.value) {
        if (this.valueReloadTries.name === this.props.name) {
            this.valueReloadTries.tries++;
        } else {
            this.valueReloadTries = {
                name: this.props.name,
                tries: 1,
            };
        }

        this.fetchOptions(selectedItems);
    }

    componentDidMount() {
        if (this.shouldValueReload()) {
            this.fetchOptionsValueReload();
        }
    }

    isAutoCompleteInvalidated(prevProps) {
        if (!this.props.autocomplete) return false;
        const statusHasChanged = prevProps.autocomplete?.status !== this.props.autocomplete.status;
        return statusHasChanged && this.props.autocomplete.status === identify.STATUS_INVALIDATED
    }

    componentDidUpdate(prevProps) {
        // if constraints changed we do not include the current items in the fetchoptions
        if (this.haveConstraintsChanged(prevProps)) {
            return this.fetchOptions(null);
        }

        if (this.isOutOfSync(prevProps)) {
            return this.fetchOptions();
        }

        if (this.isAutoCompleteInvalidated(prevProps)) {
            return this.fetchOptions();
        }

        if (this.shouldValueReload()) {
            this.fetchOptionsValueReload();
        }
    }

    shouldValueReload() {
        let value = this.props.value;

        if (!value) {
            return false;
        }

        if (this.props.multiSelect) {
            let intersec = intersection(value, map(this.props.source, 'value'));
            if (intersec.length >= this.props.value.length) {
                return false;
            }
        }
        if (findIndex(this.props.source, { value }) > -1) {
            return false;
        }

        return this.valueReloadTries.tries < this.props.maxValueReloadTries;

        // let value = [this.props.value];
        // if(this.props.multiSelect) {
        //     value = this.props.value;
        // }
        //
        // if(!value.length) { return false; }

        // if(!value.)
    }

    render() {
        return this.props.children;
    }
}

export class OptionsManagerField extends Component {
    isLoading = () => {
        if (!this.props.isAutocomplete) {
            return false;
        }
        return this.props.autocomplete.status === identify.STATUS_STARTED;
    };

    normalizedQuery = () => {
        return this.props.query ? this.props.query : '';
    };


    getSource = () => {
        let { trans } = this.props,
            query = this.normalizedQuery(),
            source = this.props.isAutocomplete
                ? remoteSource(this.props, query)
                : localSource(this.props, query);

        if (source.length < 1 && this.isLoading()) {
            return [
                {
                    text: trans('loadingPlaceholder'),
                    disabled: true,
                    value: 'system_loading',
                },
            ];
        }

        const isEmptyList = this.props.isAutocomplete && this.props.multiSelect
            ? source.length === [this.props.value ?? []].flat().length && !!query
            : source.length < 1;

        if (isEmptyList) {
            return [
                ...source,
                { text: trans('noItems'), disabled: true, value: 'system_empty' }
            ];
        }

        return source;
    };

    render() {
        if (this.props.isAutocomplete !== true) return null;
        return (
            <OptionsManager
                {...this.props} 
                source={this.getSource()}
                >
                <>{null}</>
            </OptionsManager>
        );
    }
}
export class SelectField extends OptionsManagerField {
    static propTypes = {
        dropDownLimit: PropTypes.number,
        onQueryChange: PropTypes.func,
        query: PropTypes.string,
        suggestionMatch: PropTypes.string,
        minQueryLength: PropTypes.number,
    };

    static defaultProps = {
        dropDownLimit: 10,
        suggestionMatch: 'anywhere',
        minQueryLength: 0,
    };

    isFilterable() {
        if (this.props.isAutocomplete) return true

        return !!this.props.dropDownLimit && this.props.options.length > this.props.dropDownLimit;
    }

    onChange = (value) => {
        return this.props.onChange(value);
    };

    template = (item, props) => {
        let text = get(item, 'text', '');

        try {
            text = this.renderMyHtml(text, props);
        } catch (e) {}

        return text && <React.Fragment key={item.value}>{text}</React.Fragment>;
    };

    renderMyHtml = (html, arg) => {
        // return html;

        if (!this.props.isReadonly) {
            return renderHTML(html, {
                a: LinkToSpan,
                icon: Icon,
                Text: (props) => <CollapseText onPress={arg.onPress} {...props} maxLength={40} showTruncatedText />,
            });
        }

        return renderHTML(html, {
            icon: Icon,
            Text: CollapseText,
        });
    };


    changeQuery = (query) => {
        this.props.onQueryChange?.(query);
    };

    normalizedQuery = () => {
        return this.props.query ? this.props.query : '';
    };

    focusChangeQuery = () => {
        if (this.props.options.finished_at) {
            return;
        }
        this.changeQuery(this.normalizedQuery());
    };

    renderReadonly = () => {
        let source = this.getSource(),
            value = this.getValue(),
            selected = null,
            { multiSelect, title, tooltip } = this.props;

        if (multiSelect) {
            const itemsByValue = keyBy(source, 'value');
            selected = value.map((value) => itemsByValue[value]);
        } else {
            selected = [find(source, { value })];
        }

        let readOnlyFields = selected.map((item, i) => this.template(item));

        return (
            <FormControl>
                {title && <FormControl.Label label={title} tooltip={tooltip} />}
                <Box py="10px">{readOnlyFields}</Box>
            </FormControl>
        );
    };

    getValue = () => {
        return typeof this.props.value === 'undefined' ? null : this.props.value;
    };

    deriveAddNewRecordLabel = () => {
        if (!this.props.onAddRelationRecord) return null;

        if (this.props.query) {
            return this.props.trans("addNewModelWithQuery", {
                fieldName: this.props.title,
                query: this.props.query,
            });
        }
        
        return this.props.trans("addNewModel", { fieldName: this.props.title })
    }

    renderDropdown() {
        if (this.props.isReadonly) {
            return this.renderReadonly();
        }

        let source = this.getSource();

        const SelectComponent = this.props.isInline === true ? InlineSelect : Dropdown;

        return (
            <SelectComponent
                // templateValueClassName={this.props.templateValueClassName}
                // selectedOptionClassName={this.props.selectedOptionClassName}
                // optionClassName={this.props.optionClassName}
                // small={this.props.small}
                // floating={this.props.floating}
                // className={cn(css.selectField, this.props.className)}
                labelKey="text"
                isinvalid={this.props.isinvalid}
                error={this.props.validationMessage}
                minQueryLength={this.props.minQueryLength}
                multiSelect={this.props.multiSelect}
                orderable={this.props.orderable}
                loading={this.isLoading()}
                label={this.props.title}
                tooltip={this.props.tooltip}
                onChange={this.onChange}
                source={source}
                value={this.getValue()}
                template={this.template}
                query={this.normalizedQuery()}
                allowBlank={this.props.allowBlank}
                filterHint={this.props.trans('searchPlaceholder')}
                pickOptionsHint={this.props.multiSelect ? this.props.trans('selectMultipleOptions', { title: this.props.title }) : null}
                allowFiltering={this.isFilterable()}
                onClick={this.focusChangeQuery}
                required={this.props.required}
                onQueryChange={this.changeQuery}
                disableVirtualization={this.props.disableVirtualization}
                initialNumToRender={this.props.initialNumToRender}
                onAddNewRecord={this.props.onAddRelationRecord}
                onEditRecord={this.props.onEditRelationRecord}
                addNewRecordLabel={this.deriveAddNewRecordLabel()}
            />
        );
    }

    renderField() {
        // if (this.props.isAutocomplete) {
        //     return (
        //         <OptionsManager {...this.props} source={this.getSource()}>
        //             {this.renderDropdown()}
        //         </OptionsManager>
        //     );
        // }

        return this.renderDropdown();
    }

    render() {
        return this.renderField();
    }
}
